import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { translations, languageNames, defaultLanguage } from "../lib/translations";

const LanguageContext = createContext(null);

const LANGUAGE_KEY = "moodpunk_language";

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    // Get saved language or default
    const saved = localStorage.getItem(LANGUAGE_KEY);
    if (saved && translations[saved]) {
      return saved;
    }
    // Try to detect browser language
    const browserLang = navigator.language?.split("-")[0];
    if (browserLang && translations[browserLang]) {
      return browserLang;
    }
    return defaultLanguage;
  });

  // Save language to localStorage when it changes
  const setLanguage = useCallback((lang) => {
    if (translations[lang]) {
      setLanguageState(lang);
      localStorage.setItem(LANGUAGE_KEY, lang);
    }
  }, []);

  // Get translation function
  const t = useCallback((key) => {
    const keys = key.split(".");
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === "object") {
        value = value[k];
      } else {
        return key; // Return key if translation not found
      }
    }
    
    return value || key;
  }, [language]);

  // Get all translations for current language
  const currentTranslations = translations[language] || translations[defaultLanguage];

  const value = {
    language,
    setLanguage,
    t,
    translations: currentTranslations,
    languages: languageNames,
    availableLanguages: Object.keys(translations),
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
